import * as React from 'react';

import { useEventCallback, useLiveRef, useSafeLayoutEffect } from '@gitbook/util-react-hooks';

enum LifecyclePhase {
    MOUNTING = 'MOUNTING',
    MOUNTED = 'MOUNTED',
    UNMOUNTING = 'UNMOUNTING',
    UNMOUNTED = 'UNMOUNTED',
}

/**
 * Props for the Animate component.
 */
export type AnimateCSSLifecycleProps = {
    /** Component to render, rendering null means unmount */
    children: React.ReactElement | null;

    /**
     * Callback to be called when the component is unmounted.
     * This callback is called after the animation is finished.
     */
    onUnmount?: () => void;
};

/**
 * Animations to apply during the phaes of the lifecycle
 */
export interface AnimateCSSLifecyclePhases {
    mount?: string;
    unmount?: string;
}

/**
 * Callback to listen to state of an animation.
 */
export interface AnimateCSSLifecycleCallbacks {
    onEnd?: () => void;
}

/*
 * Internal context used between AnimateLifecycle and useAnimateLifecycle.
 */

interface AnimateInternalManager<Tag extends HTMLElement> {
    bindAnimations: (
        ref: React.Ref<Tag>,
        phases: AnimateCSSLifecyclePhases,
        callbacks: AnimateCSSLifecycleCallbacks
    ) => React.Ref<Tag>;
}

const CSSLifecycleContext = React.createContext({
    bindAnimations: (ref, _phases, _callbacks) => {
        return ref;
    },
});

/**
 * Bind a view to a lifecycle animation of the upper AnimateLifeCycle.
 */
export function useAnimateCSSLifecycle<Tag extends HTMLElement>(
    phases: AnimateCSSLifecyclePhases,
    callbacks: AnimateCSSLifecycleCallbacks = {}
): React.Ref<Tag> {
    const ref = React.useRef<Tag>(null);
    const { bindAnimations } = React.useContext(CSSLifecycleContext);

    const animatedRef = bindAnimations(ref, phases, callbacks);

    return animatedRef;
}

/**
 * Implementation for the browser of the Animate component.
 * Wrap a component to animate it when it is mounted or unmounted.
 */
export function AnimateCSSLifecycle<Tag extends HTMLElement>(
    props: AnimateCSSLifecycleProps
): React.ReactElement {
    const { children, onUnmount } = props;
    const hasChildren = !!children;

    const notifyUnmount = useEventCallback(() => {
        onUnmount?.();
    });
    const [descendants] = React.useState(
        () =>
            new Map<
                React.Ref<HTMLElement>,
                {
                    phases: AnimateCSSLifecyclePhases;
                    element: HTMLElement;
                    callbacks: AnimateCSSLifecycleCallbacks;
                }
            >()
    );

    const manager = React.useMemo<AnimateInternalManager<Tag>>(() => {
        return {
            bindAnimations: (ref, phases, callbacks) => {
                return (element) => {
                    if (element) {
                        descendants.set(ref, {
                            phases,
                            callbacks,
                            element,
                        });
                    } else {
                        descendants.delete(ref);
                    }
                };
            },
        };
    }, [descendants]);

    /**
     * Start an animation and returns a callback to cancel it.
     */
    const animate = useEventCallback(
        (animationPhase: 'mount' | 'unmount', onFinished: () => void) => {
            // Count of animations to wait for.
            let pending = 0;

            /** Callback when the animation is finished for all elements */
            const onAnimationEnd = () => {
                onFinished();
            };

            if (descendants.size === 0) {
                onAnimationEnd();
                return () => {};
            }

            const cleanups = [...descendants.entries()].map(
                ([_ref, { phases, element, callbacks }]) => {
                    // @ts-ignore we know we are using RNW
                    const animation = phases[animationPhase];

                    if (!element || !animation) {
                        return;
                    }

                    const onElementAnimationEnd = () => {
                        if (animationPhase === 'unmount') {
                            // Hide element to emulate the unmount until we remove it from the DOM
                            // We use opacity instead of display:none to ensure it doesn't cancel
                            // other potential animations.
                            element.style.visibility = 'hidden';
                        }

                        pending -= 1;
                        if (pending === 0) {
                            onAnimationEnd();
                        }

                        callbacks.onEnd?.();
                    };

                    /** Callbak when one of the element finished animating */
                    const onElementAnimationEndEvent = (event: Event) => {
                        const element = event.currentTarget as Tag;

                        if (event && element !== event.target) {
                            // If the event was generated for something NOT this element we
                            // should ignore it as it's not relevant to us
                            return;
                        }

                        onElementAnimationEnd();
                    };

                    pending += 1;

                    // Start the animation
                    let elementAnimation: Animation | undefined;
                    const frameId = window.requestAnimationFrame(() => {
                        elementAnimation = animateElement(element, animation);

                        // Listen to the animation finished - it might also be removed or cancelled depending
                        // on the state of the DOM.
                        elementAnimation?.addEventListener('finish', onElementAnimationEndEvent);
                        elementAnimation?.addEventListener('remove', (e) => {
                            onElementAnimationEndEvent(e);
                        });
                        elementAnimation?.addEventListener('cancel', (e) => {
                            onElementAnimationEndEvent(e);
                        });
                    });

                    return () => {
                        window.cancelAnimationFrame(frameId);
                        elementAnimation?.cancel();
                        onElementAnimationEnd();
                    };
                }
            );

            return () => {
                cleanups.forEach((cleanup) => {
                    if (cleanup) {
                        cleanup();
                    }
                });
            };
        }
    );

    const [phase, setPhase] = React.useState(
        hasChildren ? LifecyclePhase.MOUNTING : LifecyclePhase.UNMOUNTED
    );
    const phaseRef = useLiveRef(phase);

    //
    // Transition to the next phase when the component is rendered as mounted/unmounted
    //
    useSafeLayoutEffect(() => {
        if (hasChildren) {
            setPhase(LifecyclePhase.MOUNTING);
        } else if (phaseRef.current !== LifecyclePhase.UNMOUNTED) {
            setPhase(LifecyclePhase.UNMOUNTING);
        }
    }, [hasChildren, phaseRef]);

    //
    // Animate in a second effect to ensure the component has been rendered with the right phase
    // and the refs are ready
    //
    useSafeLayoutEffect(() => {
        let cancelled = false;
        let stopAnimation = () => {};

        if (phase === LifecyclePhase.MOUNTING) {
            stopAnimation = animate('mount', () => {
                if (!cancelled) {
                    setPhase(LifecyclePhase.MOUNTED);
                }
            });
        } else if (phase === LifecyclePhase.UNMOUNTING) {
            stopAnimation = animate('unmount', () => {
                if (!cancelled) {
                    setPhase(LifecyclePhase.UNMOUNTED);
                    notifyUnmount();
                }
            });
        }

        return () => {
            cancelled = true;
            stopAnimation();
        };
    }, [phase, notifyUnmount, animate]);

    // Keep track of the latest element rendered to continue rendering it during the unmount phase.
    const latestChildrenRef = React.useRef<typeof children>(null);
    useSafeLayoutEffect(() => {
        if (children) {
            latestChildrenRef.current = React.cloneElement(children);
        }
    }, [children]);

    // While unmounting, we keep rendering the previous children
    const render = () => {
        if (children) {
            return children;
        }

        if (phase === LifecyclePhase.UNMOUNTING || phase === LifecyclePhase.MOUNTING) {
            return latestChildrenRef.current;
        }

        return null;
    };

    return <CSSLifecycleContext.Provider value={manager}>{render()}</CSSLifecycleContext.Provider>;
}

/**
 * Animate an element and return the animation.
 */
function animateElement<Tag extends HTMLElement>(
    element: Tag,
    animationClassName: string
): Animation | undefined {
    element.classList.add(animationClassName);

    const animations = element.getAnimations();
    const animation = animations[0];

    return animation;
}
